import {
  DEFAULT_SCRIPT_WEB_APP_URL,
  SCRIPT_WEB_APP_URL_KEY,
  SESSION_STORAGE_KEY
} from '../constants';

const CHUNK_UPLOAD_THRESHOLD_BYTES = 5 * 1024 * 1024;
const CHUNK_BASE64_CHARS = 4 * 1024 * 1024; // ~3 MB decoded, safely below Apps Script POST limits.

function toFormBody(obj) {
  const p = new URLSearchParams();
  Object.entries(obj || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    p.append(key, typeof value === 'string' ? value : JSON.stringify(value));
  });
  return p.toString();
}

function parseDataUrl(dataUrl) {
  const match = String(dataUrl || '').match(/^data:([^;]+);base64,(.+)$/s);
  if (!match) return null;
  const base64 = match[2];
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  const byteLength = Math.floor((base64.length * 3) / 4) - padding;
  return { mimeType: match[1], base64, byteLength };
}

async function postForm(scriptUrl, payload) {
  const response = await fetch(scriptUrl, {
    method: 'POST',
    mode: 'cors',
    redirect: 'follow',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
    body: toFormBody(payload)
  });
  const text = await response.text();
  let result;
  try { result = JSON.parse(text); } catch { return { ok: false, error: 'Invalid server response' }; }
  if (!response.ok) return { ok: false, error: result?.error || `HTTP ${response.status}` };
  return result;
}

function mergeRemoteAttachment(attachment, remote) {
  const driveUrl = attachment.type === 'image' && remote.driveFileId
    ? `https://drive.google.com/uc?export=view&id=${encodeURIComponent(remote.driveFileId)}`
    : remote.driveUrl;

  return {
    ...attachment,
    ...remote,
    driveUrl,
    addedAt: attachment.addedAt,
    width: attachment.width,
    height: attachment.height,
    rotation: attachment.rotation || 0,
    dataRef: attachment.dataRef
  };
}

async function uploadSmallAttachment(scriptUrl, sessionToken, attachment, noteId, parsed) {
  const result = await postForm(scriptUrl, {
    action: 'upload_attachment',
    sessionToken,
    noteId,
    attachmentId: attachment.id,
    fileName: attachment.name || (attachment.type === 'image' ? 'screenshot.jpg' : 'attachment'),
    mimeType: attachment.mimeType || parsed.mimeType || (attachment.type === 'image' ? 'image/jpeg' : 'application/octet-stream'),
    dataUrl: attachment.dataRef,
    caption: attachment.caption || ''
  });
  return result?.ok && result?.attachment ? mergeRemoteAttachment(attachment, result.attachment) : attachment;
}

async function uploadLargeAttachment(scriptUrl, sessionToken, attachment, noteId, parsed) {
  const fileName = attachment.name || (attachment.type === 'image' ? 'screenshot.jpg' : 'attachment');
  const mimeType = attachment.mimeType || parsed.mimeType || 'application/octet-stream';

  const start = await postForm(scriptUrl, {
    action: 'upload_attachment_start',
    sessionToken,
    noteId,
    attachmentId: attachment.id,
    fileName,
    mimeType,
    totalBytes: parsed.byteLength,
    caption: attachment.caption || ''
  });
  if (!start?.ok || !start?.uploadSession) return attachment;

  let byteOffset = 0;
  let finalAttachment = null;
  for (let charOffset = 0; charOffset < parsed.base64.length; charOffset += CHUNK_BASE64_CHARS) {
    const chunkBase64 = parsed.base64.slice(charOffset, charOffset + CHUNK_BASE64_CHARS);
    const chunkResult = await postForm(scriptUrl, {
      action: 'upload_attachment_chunk',
      sessionToken,
      uploadSession: start.uploadSession,
      offset: byteOffset,
      totalBytes: parsed.byteLength,
      mimeType,
      attachmentId: attachment.id,
      fileName,
      caption: attachment.caption || '',
      chunkBase64
    });
    if (!chunkResult?.ok) return attachment;
    byteOffset = Number(chunkResult.nextOffset || byteOffset);
    if (chunkResult.attachment) finalAttachment = chunkResult.attachment;
  }

  return finalAttachment ? mergeRemoteAttachment(attachment, finalAttachment) : attachment;
}

export async function uploadAttachmentSilently(attachment, noteId) {
  if (!attachment?.dataRef || !navigator.onLine) return attachment;

  let session;
  try { session = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}'); } catch { session = {}; }
  if (!session?.token) return attachment;

  const scriptUrl = localStorage.getItem(SCRIPT_WEB_APP_URL_KEY) || DEFAULT_SCRIPT_WEB_APP_URL;
  if (!scriptUrl) return attachment;

  const parsed = parseDataUrl(attachment.dataRef);
  if (!parsed) return attachment;

  try {
    if (parsed.byteLength > CHUNK_UPLOAD_THRESHOLD_BYTES) {
      return await uploadLargeAttachment(scriptUrl, session.token, attachment, noteId, parsed);
    }
    return await uploadSmallAttachment(scriptUrl, session.token, attachment, noteId, parsed);
  } catch (err) {
    console.warn('Silent attachment upload deferred', attachment?.name, err);
    return attachment;
  }
}